import { ReloadOutlined, RetweetOutlined } from '@ant-design/icons';
import { Button, Descriptions, Drawer, Space, Table, Tag, Typography, message } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getPublishTaskRun, listPublishJobsByRun, retryPublishTaskRun } from '../api';
import { formatErrorMessage } from '../utils/errorMessage';
import { formatShanghaiDateTime } from '../utils/datetime';
import { getAgentTypeLabel, getExecutionPolicyLabel } from '../utils/agentExecutionPolicy';

const { Text } = Typography;

const STATUS_TEXT = {
  pending: '待执行',
  running: '运行中',
  success: '成功',
  noop: '无操作',
  failed: '失败',
  canceled: '已取消',
};

const statusTag = (status) => {
  const s = String(status || '').toLowerCase();
  if (!s) return <Tag>-</Tag>;
  if (s === 'success') return <Tag color="green">{STATUS_TEXT[s]}</Tag>;
  if (s === 'failed') return <Tag color="red">{STATUS_TEXT[s]}</Tag>;
  if (s === 'noop') return <Tag color="blue">{STATUS_TEXT[s]}</Tag>;
  if (s === 'running') return <Tag color="gold">{STATUS_TEXT[s]}</Tag>;
  if (s === 'pending') return <Tag color="gold">{STATUS_TEXT[s]}</Tag>;
  if (s === 'canceled') return <Tag>{STATUS_TEXT[s]}</Tag>;
  return <Tag>{STATUS_TEXT[s] || s}</Tag>;
};

const formatDateTime = (dateTimeString) => {
  return formatShanghaiDateTime(dateTimeString);
};

const isWeixinChannelsPlatform = (value) => {
  const code = String(value || '').trim().toLowerCase();
  return code === 'weixin_channels' || code === 'wechat_channels' || code === '微信视频号';
};

const renderPlatformPostUrl = (_, record) => {
  const url = String(record?.platform_post_url || '').trim();
  if (url) {
    return (
      <a href={url} target="_blank" rel="noopener noreferrer">
        打开作品
      </a>
    );
  }
  if (isWeixinChannelsPlatform(record?.platform_code) && String(record?.status || '').toLowerCase() === 'success') {
    return <Text type="secondary">微信视频号暂不保证直达链接</Text>;
  }
  return <Text type="secondary">-</Text>;
};

const PublishRunViewer = ({ open, taskRunId, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [run, setRun] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [jobsTotal, setJobsTotal] = useState(0);
  const [jobsPage, setJobsPage] = useState(1);
  const [jobsPageSize, setJobsPageSize] = useState(50);
  const [retryingType, setRetryingType] = useState('');

  const reload = useCallback(async (pageOverride = null, pageSizeOverride = null) => {
    const id = String(taskRunId || '').trim();
    if (!open || !id) return;
    setLoading(true);
    try {
      const [runResp, jobsResp] = await Promise.all([
        getPublishTaskRun(id),
        listPublishJobsByRun(id, { page: pageOverride ?? jobsPage, page_size: pageSizeOverride ?? jobsPageSize }),
      ]);

      if (runResp?.status === 'success') {
        setRun(runResp.run || null);
      } else if (runResp?.status === 'not_found') {
        setRun(null);
      }

      if (jobsResp?.status === 'success') {
        setJobs(Array.isArray(jobsResp.items) ? jobsResp.items : []);
        setJobsTotal(jobsResp.total || 0);
        setJobsPage(jobsResp.page || 1);
        setJobsPageSize(jobsResp.page_size || jobsPageSize);
      }
    } catch (e) {
      message.error(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [jobsPage, jobsPageSize, open, taskRunId]);

  useEffect(() => {
    if (!open) return;
    setJobsPage(1);
  }, [open, taskRunId]);

  useEffect(() => {
    reload(1, jobsPageSize);
  }, [open, reload, taskRunId, jobsPageSize]);

  const handleRetryWithSwitch = useCallback(async (agentType) => {
    const id = String(taskRunId || '').trim();
    if (!id) return;
    setRetryingType(agentType);
    try {
      const resp = await retryPublishTaskRun(id, {
        override_agent_type: agentType,
        switch_reason: 'manual_switch_after_failure',
      });
      if (resp?.status === 'accepted') {
        message.success(`已发起重试（切换到${agentType === 'local' ? '本地' : '云端'}）`);
        const nextRunId = String(resp?.new_task_run_id || '').trim();
        if (nextRunId) {
          setJobs([]);
          setRun(null);
          await reload(1, jobsPageSize);
        }
      } else {
        message.error(resp?.error_message || '发起重试失败');
      }
    } catch (error) {
      message.error(error?.message || '发起重试失败');
    } finally {
      setRetryingType('');
    }
  }, [jobsPageSize, reload, taskRunId]);

  const columns = useMemo(() => ([
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (v) => statusTag(v),
    },
    {
      title: '平台',
      dataIndex: 'platform_code',
      key: 'platform_code',
      width: 140,
      render: (v) => <Text>{v || '-'}</Text>,
    },
    {
      title: '计划节点',
      key: 'planned_agent',
      width: 180,
      render: (_, record) => {
        const t = String(record?.planned_agent_type || '').trim();
        const id = String(record?.planned_agent_id || '').trim();
        if (!t && !id) return <Text type="secondary">-</Text>;
        return <Text>{getAgentTypeLabel(t)}{id ? ` · ${id}` : ''}</Text>;
      },
    },
    {
      title: '实际节点',
      key: 'actual_agent',
      width: 180,
      render: (_, record) => {
        const t = String(record?.final_agent_type || '').trim();
        const id = String(record?.final_agent_id || '').trim();
        if (!t && !id) return <Text type="secondary">-</Text>;
        return <Text>{getAgentTypeLabel(t)}{id ? ` · ${id}` : ''}</Text>;
      },
    },
    {
      title: '账号名',
      dataIndex: 'account_name',
      key: 'account_name',
      width: 160,
      ellipsis: true,
      render: (v) => {
        const msg = formatErrorMessage(v);
        return <Text title={msg || ''}>{msg || '-'}</Text>;
      },
    },
    {
      title: '账号ID',
      dataIndex: 'account_id',
      key: 'account_id',
      width: 240,
      render: (v) => <Text code>{v || '-'}</Text>,
    },
    {
      title: '标题',
      dataIndex: 'content_title',
      key: 'content_title',
      width: 260,
      ellipsis: true,
      render: (v) => <Text title={v || ''}>{v || '-'}</Text>,
    },
    {
      title: '内容ID',
      dataIndex: 'content_id',
      key: 'content_id',
      width: 240,
      render: (v) => <Text code>{v || '-'}</Text>,
    },
    {
      title: '作品ID',
      dataIndex: 'remote_post_id',
      key: 'remote_post_id',
      width: 240,
      render: (v) => (
        v ? <Text code copyable={{ text: v }}>{v}</Text> : <Text code>-</Text>
      ),
    },
    {
      title: '作品链接',
      dataIndex: 'platform_post_url',
      key: 'platform_post_url',
      width: 220,
      render: renderPlatformPostUrl,
    },
    {
      title: '错误',
      dataIndex: 'error_message',
      key: 'error_message',
      ellipsis: true,
      render: (v) => <Text title={v || ''}>{v || '-'}</Text>,
    },
  ]), []);

  const id = String(taskRunId || '').trim();

  return (
    <Drawer
      title="发布任务运行详情"
      open={open}
      onClose={onClose}
      width={1100}
      destroyOnClose
      extra={(
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => reload()} loading={loading}>
            刷新
          </Button>
        </Space>
      )}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Descriptions size="small" column={1} bordered>
          <Descriptions.Item label="运行ID">
            <Text code copyable={{ text: id }}>{id || '-'}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="任务ID">
            <Text code>{run?.task_id || '-'}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="状态">
            {statusTag(run?.status)}
          </Descriptions.Item>
          <Descriptions.Item label="描述">
            <Text>{run?.desc || '-'}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="开始时间">
            <Text>{formatDateTime(run?.started_at)}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="结束时间">
            <Text>{formatDateTime(run?.ended_at)}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="错误">
            <Text>{formatErrorMessage(run?.error_message || run?.error_code) || '-'}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="执行策略">
            <Text>{getExecutionPolicyLabel(run?.execution_policy)}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="计划节点">
            <Text>{getAgentTypeLabel(run?.planned_agent_type)}{run?.planned_agent_id ? ` · ${run.planned_agent_id}` : ''}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="实际节点">
            <Text>{getAgentTypeLabel(run?.actual_agent_type)}{run?.actual_agent_id ? ` · ${run.actual_agent_id}` : ''}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="切换原因">
            <Text>{run?.switch_reason || '-'}</Text>
          </Descriptions.Item>
        </Descriptions>

        {(String(run?.status || '').toLowerCase() === 'failed' || String(run?.status || '').toLowerCase() === 'canceled') ? (
          <Space>
            <Button
              icon={<RetweetOutlined />}
              onClick={() => handleRetryWithSwitch('local')}
              loading={retryingType === 'local'}
            >
              切到本地重试
            </Button>
            <Button
              icon={<RetweetOutlined />}
              onClick={() => handleRetryWithSwitch('cloud')}
              loading={retryingType === 'cloud'}
            >
              切到云端重试
            </Button>
          </Space>
        ) : null}

        <div>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>发布作业列表</div>
          <Table
            size="small"
            rowKey={(r) => r.job_id || `${r.run_id}:${r.account_id}:${r.content_id}`}
            loading={loading}
            columns={columns}
            dataSource={jobs}
            pagination={{
              current: jobsPage,
              pageSize: jobsPageSize,
              total: jobsTotal,
              showSizeChanger: true,
              onChange: (p, ps) => {
                setJobsPage(p);
                setJobsPageSize(ps);
                reload(p, ps);
              },
            }}
            scroll={{ x: 'max-content' }}
            locale={{ emptyText: '暂无作业' }}
          />
        </div>
      </Space>
    </Drawer>
  );
};

export default PublishRunViewer;
